/**
 * @fileoverview Shared, framework-agnostic MSAL token helpers used by every
 * FireSight web client (tenant-web, system-web, mobile-pwa).
 * @module auth
 *
 * Responsibilities:
 * - Decode a JWT's `exp` claim and decide if it is expired (DRY: previously
 *   copy-pasted as `isTokenExpired` / `isJwtExpired` in three apps).
 * - Acquire a fresh API bearer token from MSAL with a proactive expiry buffer
 *   and a forceRefresh fallback (the proven tenant-web pattern).
 *
 * Dependencies:
 * - `@azure/msal-browser` (types + acquireTokenSilent) as a peer dependency.
 *
 * Architecture:
 * - Pure logic only. NO React, NO app-specific provider/offline/impersonation
 *   concerns. Each app keeps its own AuthProvider and decides UX for
 *   InteractionRequiredAuthError (which this module rethrows to the caller).
 * - Consumed as TypeScript source (main/types → src/index.ts), mirroring
 *   `@firesquirrel/local-db`.
 *
 * Notes:
 * - The idToken (aud === clientId) is the API token validated by the FireSight
 *   APIs; the accessToken targets Microsoft Graph. Callers prefer idToken.
 */

import type {
  AccountInfo,
  AuthenticationResult,
  IPublicClientApplication,
} from '@azure/msal-browser';

/**
 * Decode the base64url payload segment of a JWT. Returns null on any failure.
 */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * @contract isJwtExpired
 * Purpose: Single source of truth for "is this bearer token unusable?".
 * Fields:  token (JWT string), bufferSeconds (clock-skew safety margin).
 * Rules:
 *  - Malformed/unparseable token ⇒ treated as EXPIRED (true). Sending it would
 *    only earn a 401, so callers must keep waiting for a real token.
 *  - A token without an `exp` claim ⇒ treated as NOT expired (false), matching
 *    the prior per-app behavior.
 *  - Otherwise expired when (exp - bufferSeconds) <= now.
 * Sync:    Used by the api-client gate and the AuthProvider session checks.
 */
export function isJwtExpired(token: string, bufferSeconds = 30): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload) return true;
  const exp = payload.exp as number | undefined;
  if (!exp) return false;
  return exp - bufferSeconds < Date.now() / 1000;
}

/**
 * Options controlling proactive refresh behavior for {@link acquireFreshToken}.
 */
export interface AcquireFreshTokenOptions {
  /**
   * Force a network token renewal when the acquired token is within this many
   * milliseconds of its `exp`. Defaults to 5 minutes — identical to tenant-web.
   */
  proactiveRefreshBufferMs?: number;
  /**
   * When true, the FIRST acquireTokenSilent call is made with
   * `forceRefresh: true` (a caller explicitly demanded a network renewal,
   * e.g. system-web's token provider `options.forceRefresh`). The proactive
   * buffer check still applies afterward. Defaults to false.
   */
  forceRefresh?: boolean;
}

/**
 * @process acquireFreshToken
 *
 * Steps:
 * 1. Silently acquire a token for the given account/scopes.
 * 2. Prefer the idToken (aud === clientId, validated by FireSight APIs); fall
 *    back to the accessToken.
 * 3. If that token is within the proactive buffer of expiry, re-acquire with
 *    `forceRefresh: true` so we never hand a caller a token that is about to /
 *    has already expired (the root cause of the mobile 401 NO_TOKEN storms).
 *
 * Inputs:
 * - instance: an initialized MSAL PublicClientApplication.
 * - account:  the signed-in MSAL account.
 * - scopes:   login scopes (e.g. ['openid','profile','email']).
 * - opts:     optional proactive refresh buffer.
 *
 * Outputs:
 * - A fresh bearer token string, or null when MSAL returned no token.
 *
 * Failure Handling:
 * - Does NOT swallow errors. `acquireTokenSilent` throws (notably
 *   InteractionRequiredAuthError) propagate to the caller so each app applies
 *   its own offline guard and session-expired UX.
 *
 * Idempotency:
 * - REQUIRED (safe to call on every request; this is the pull model).
 */
export async function acquireFreshToken(
  instance: IPublicClientApplication,
  account: AccountInfo,
  scopes: string[],
  opts?: AcquireFreshTokenOptions,
): Promise<string | null> {
  let response: AuthenticationResult = await instance.acquireTokenSilent({
    scopes,
    account,
    forceRefresh: opts?.forceRefresh ?? false,
  });
  let token = response.idToken || response.accessToken;

  if (token) {
    // Only force a refresh when we can actually read an `exp` AND it is within
    // the buffer. A token whose payload cannot be decoded is used as-is — this
    // matches the original per-app behavior (decode failure ⇒ no forced
    // refresh) and avoids hammering acquireTokenSilent on opaque tokens.
    const payload = decodeJwtPayload(token);
    const exp = payload?.exp as number | undefined;
    if (exp) {
      const expiresAtMs = exp * 1000;
      const bufferMs = opts?.proactiveRefreshBufferMs ?? 5 * 60 * 1000;
      if (Date.now() > expiresAtMs - bufferMs) {
        response = await instance.acquireTokenSilent({ scopes, account, forceRefresh: true });
        token = response.idToken || response.accessToken;
      }
    }
  }

  return token ?? null;
}
